import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const M = "/module-rbac/hris";
const ICON = { attendance_computation: "Calculator", payroll_master: "BookUser", holidays: "CalendarDays", work_schedules: "CalendarClock",
  leave_types: "ListChecks", employee_allowance: "Wallet", employee_deductions: "ReceiptText", employee_leave_balances: "Scale" };

const Picker = ({ item, users, roles, onClose, onSaved }) => {
  const [u, setU] = useState(new Set(item.user_ids));
  const [r, setR] = useState(new Set(item.role_ids));
  const [q, setQ] = useState("");
  const list = useMemo(() => users.filter((x) => !q || `${x.name} ${x.email}`.toLowerCase().includes(q.toLowerCase())).slice(0, 80), [users, q]);
  const flip = (set, setter, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); setter(n); };
  const save = async () => {
    try { await api.put(`${M}/approvals/${item.type}`, { user_ids: [...u], role_ids: [...r] }); toast.success("Approvers saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hris-approval-dialog">
        <DialogHeader><DialogTitle>Approvers · {item.label}</DialogTitle><DialogDescription>Pick whole roles and/or specific users. They get a notification whenever a {item.label} record is added, changed or deleted.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Roles</div>
            {roles.length === 0 && <div className="text-xs text-slate-400">No HRIS roles yet. Create one under Roles & Permissions.</div>}
            <div className="flex flex-wrap gap-2">{roles.map((x) => (
              <label key={x.id} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm cursor-pointer ${r.has(x.id) ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200"}`}>
                <Checkbox checked={r.has(x.id)} onCheckedChange={() => flip(r, setR, x.id)} data-testid={`hris-approval-role-${x.id}`} />{x.name}</label>))}</div></div>
          <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Users</div>
            <Input placeholder="Search users" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" data-testid="hris-approval-user-search" />
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y">{list.map((x) => (
              <label key={x.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={u.has(x.user_id)} onCheckedChange={() => flip(u, setU, x.user_id)} data-testid={`hris-approval-user-${x.user_id}`} />
                <span className="min-w-0"><span className="font-medium">{x.name}</span><span className="block text-xs text-slate-500 truncate">{x.email} · {x.role_id === "employee" ? "Employee" : x.role}</span></span></label>))}</div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="hris-approval-save">Save Approvers</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function HrisApprovals() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    const [a, u, r] = await Promise.all([api.get(`${M}/approvals`), api.get(`${M}/users`), api.get(`${M}/roles`)]);
    setItems(a.data); setUsers(u.data.filter((x) => x.user_id)); setRoles(r.data.filter((x) => !x.builtin));
  };
  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Could not load")); }, []);
  const uname = (id) => users.find((x) => x.user_id === id)?.name || "Unknown user";
  const rname = (id) => roles.find((x) => x.id === id)?.name || "Deleted role";
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm text-slate-700" data-testid="hris-approvals-intro">
        <Icons.Info className="h-4 w-4 inline mr-1.5 text-blue-600" />Changes apply right away. Designated approvers are notified so they can review each change.
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((it) => {
          const I = Icons[ICON[it.type]] || Icons.CheckCircle2;
          const none = !it.user_ids.length && !it.role_ids.length;
          return (
            <div key={it.type} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3" data-testid={`hris-approval-${it.type}`}>
              <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center shrink-0"><I className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2"><div className="font-semibold text-slate-900">{it.label}</div>
                  <Button size="sm" variant="outline" onClick={() => setEdit(it)} data-testid={`hris-approval-edit-${it.type}`}><Icons.UserPlus className="h-3.5 w-3.5 mr-1" />Designate</Button></div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {none && <span className="text-xs text-slate-400">No approvers yet</span>}
                  {it.role_ids.map((id) => <span key={id} className="text-xs rounded-full border border-violet-200 bg-violet-50 text-violet-700 px-2 py-0.5">Role: {rname(id)}</span>)}
                  {it.user_ids.map((id) => <span key={id} className="text-xs rounded-full border border-slate-200 bg-slate-50 text-slate-700 px-2 py-0.5">{uname(id)}</span>)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {edit && <Picker item={edit} users={users} roles={roles} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      <MemoApprovers />
    </div>
  );
}

function MemoApprovers() {
  const [levels, setLevels] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/hris/payroll-memo/approvers").then(({ data }) => {
    setLevels(data); setSel(Object.fromEntries(data.map((l) => [l.level, l.user?.id || ""])));
  }).catch(() => {});
  useEffect(() => { load(); api.get("/hris/payroll-memo/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/hris/payroll-memo/approvers", sel); toast.success("Payroll Memo approvers saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mt-2" data-testid="memo-approvers">
      <div className="flex items-center justify-between gap-2 mb-1"><div className="font-semibold text-slate-900 flex items-center gap-2"><Icons.FileSignature className="h-4 w-4 text-emerald-700" />Payroll Memo Approvers</div>
        <Button size="sm" onClick={save} disabled={busy} data-testid="memo-approvers-save">Save</Button></div>
      <p className="text-xs text-slate-500 mb-3">The 5 signing levels of every Payroll Memo, in order. Each person needs an e-signature uploaded in Profile › Security.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">{levels.map((l, i) => (
        <div key={l.level} className="space-y-1" data-testid={`memo-approver-${l.level}`}>
          <div className="text-[11px] font-semibold text-slate-500">{i + 1}. {l.title}</div>
          <div className="text-xs text-blue-700">{l.rank}</div>
          <select value={sel[l.level] || ""} onChange={(e) => setSel({ ...sel, [l.level]: e.target.value })} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid={`memo-approver-select-${l.level}`}>
            <option value="">— Select user —</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
          {l.user && <div className={`text-[11px] ${l.user.has_signature ? "text-emerald-700" : "text-amber-700"}`}>{l.user.has_signature ? "e-Signature on file" : "No e-signature yet"}</div>}
        </div>))}</div>
    </div>
  );
}
